/**
 * Zoom da interface (2026-08-27; memória entre aberturas em 2026-11-04).
 *
 * Zoom PROPRIO do app — CSS `zoom` na raiz — em vez do zoom nativo do
 * navegador/webview. Diferenca que o usuario pediu: o conteudo REFLUI (nao
 * fica fixo nem cortado) e a proporcao segue igual ao navegar, porque a SPA
 * nao recarrega e o valor fica na raiz do documento.
 *
 * Existe UM zoom, lembrado (SPEC-20261104-1534, WI-1072): o ultimo que o
 * usuario definiu — por Preferencias, Ctrl +/-, Ctrl+scroll ou Ctrl+0 — e o
 * que vale ao abrir o Orbind e ao dar F5. Antes, Ctrl +/- era so da sessao e
 * o F5 voltava ao "padrao" das Preferencias; monitores menores precisavam
 * refazer o ajuste toda hora.
 *
 * SO no Orbind Desktop: no navegador comum o proprio browser ja faz o zoom
 * (reflui, persiste, Ctrl +/-), entao aqui seria zoom em cima de zoom. No
 * desktop, este CSS substitui o zoom nativo do webview que deixava a area
 * "fixa" (nao refluia) — o pedido do usuario.
 */

import { isDesktop } from './platform.ts'

/** Mesma chave de antes: quem ja tinha um padrao gravado continua com ele. */
export const CHAVE_DO_ZOOM = 'orbind.zoom.default'
export const ZOOM_MIN = 50
export const ZOOM_MAX = 200
export const ZOOM_PASSO = 10

/** Arredonda para o passo e prende no intervalo permitido. */
export function normalizarZoom(pct: number): number {
  const passo = Math.round(pct / ZOOM_PASSO) * ZOOM_PASSO
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, passo))
}

/** Zoom lembrado (o ultimo definido pelo usuario); 100 quando nunca mexeu. */
export function zoomLembrado(): number {
  try {
    const v = Number(localStorage.getItem(CHAVE_DO_ZOOM))
    return Number.isFinite(v) && v > 0 ? normalizarZoom(v) : 100
  } catch {
    return 100
  }
}

function lembrar(pct: number) {
  try {
    localStorage.setItem(CHAVE_DO_ZOOM, String(pct))
  } catch {
    /* sem persistencia: vale so nesta sessao */
  }
}

let atual = 100
const ouvintes = new Set<(pct: number) => void>()

function notificar() {
  for (const f of ouvintes) f(atual)
}

function aplicar(pct: number) {
  // So mexe na raiz no DESKTOP; no navegador o zoom e do proprio browser.
  if (!isDesktop()) return
  // CSS `zoom` na raiz: reflui o layout (diferente de transform: scale, que
  // so escala e deixa o conteudo do mesmo tamanho logico).
  if (pct === 100) document.documentElement.style.removeProperty('zoom')
  else document.documentElement.style.setProperty('zoom', String(pct / 100))
}

/** @deprecated use `zoomLembrado` — mantido para quem ainda chama pelo nome antigo. */
export function zoomPadrao(): number {
  return zoomLembrado()
}

/** Zoom vivo aplicado agora. */
export function zoomAtual(): number {
  return atual
}

/**
 * Define o zoom: aplica na hora E lembra para a proxima abertura/F5.
 * Retorna o valor final (normalizado).
 */
export function definirZoom(pct: number): number {
  atual = normalizarZoom(pct)
  lembrar(atual)
  aplicar(atual)
  notificar()
  return atual
}

/** @deprecated use `definirZoom` — Preferencias e atalhos gravam o mesmo valor. */
export function definirZoomPadrao(pct: number): number {
  return definirZoom(pct)
}

/** @deprecated use `definirZoom` — nao existe mais zoom "so da sessao". */
export function definirZoomVivo(pct: number): number {
  return definirZoom(pct)
}

export function ajustarZoom(delta: number) {
  definirZoom(atual + delta)
}

/** Ctrl+0: volta ao tamanho normal (100%) — e isso tambem fica lembrado. */
export function restaurarZoom() {
  definirZoom(100)
}

/** Assina mudancas do zoom (para a UI refletir). Dispara o valor atual na hora. */
export function assinarZoom(f: (pct: number) => void): () => void {
  ouvintes.add(f)
  f(atual)
  return () => {
    ouvintes.delete(f)
  }
}

/**
 * Aplica o zoom lembrado e liga os atalhos (Ctrl +/-/0 e Ctrl+scroll).
 * Chamar UMA vez no boot. Os atalhos usam preventDefault para substituir o
 * zoom nativo do webview — assim so existe um zoom, o que reflui.
 */
export function iniciarZoom() {
  // No navegador nao faz nada: o zoom e nativo do browser (evita zoom duplo).
  if (!isDesktop()) return
  atual = zoomLembrado()
  aplicar(atual)
  notificar()

  window.addEventListener(
    'keydown',
    (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      if (e.key === '=' || e.key === '+') {
        e.preventDefault()
        ajustarZoom(ZOOM_PASSO)
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        ajustarZoom(-ZOOM_PASSO)
      } else if (e.key === '0') {
        e.preventDefault()
        restaurarZoom()
      }
    },
    { passive: false },
  )

  window.addEventListener(
    'wheel',
    (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      ajustarZoom(e.deltaY < 0 ? ZOOM_PASSO : -ZOOM_PASSO)
    },
    { passive: false },
  )
}
